/**
 * The custom date picker every time filter shares: a range panel with shortcuts, typed From / To
 * fields and a calendar (two months on a wide screen, one in a bottom sheet on a phone), the
 * dropdown and calendar-tab triggers that open it, and a single-day field for forms.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import {
  addDays,
  addMonths,
  dayLabel,
  dayToTyped,
  isTime,
  monthStart,
  RANGE_SHORTCUTS,
  rangeLabel,
  rangeProblem,
  spanDays,
  todayIn,
  typedToDay,
  weekday,
  type DayRange,
  type Zone,
} from '@platform/shared';
import { useMedia } from '../lib/media';
import { cx, Icon } from './ui';

export type { DayRange, Zone };

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const PHONE_QUERY = '(max-width: 639px)';
const TWO_MONTHS_QUERY = '(min-width: 760px)';

/** "UTC", or the viewer's offset now ("GMT+1"). */
export function zoneLabel(zone: Zone): string {
  if (zone === 'utc') return 'UTC';
  const off = -new Date().getTimezoneOffset();
  if (!off) return 'GMT';
  const h = Math.floor(Math.abs(off) / 60);
  const m = Math.abs(off) % 60;
  return `GMT${off > 0 ? '+' : '−'}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
}

export interface RangeOptions {
  /** Whose calendar the days are: the viewer's (default) or UTC, for screens built on UTC days. */
  zone?: Zone;
  /** Earliest day that can be picked. */
  min?: string;
  /** Longest range allowed, in days. */
  maxDays?: number;
  /** Offers "Include times" (admin screens). */
  times?: boolean;
}

// ---- Calendar ----

type MonthProps = {
  month: string;
  today: string;
  from: string | null;
  to: string | null;
  hover: string | null;
  focus: string;
  disabled: (day: string) => boolean;
  onPick: (day: string) => void;
  onHover: (day: string | null) => void;
  prev?: () => void;
  next?: () => void;
  canNext: boolean;
  large?: boolean;
};

function Month({ month, today, from, to, hover, focus, disabled, onPick, onHover, prev, next, canNext, large }: MonthProps) {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const lead = weekday(month);
  const count = spanDays(month, addDays(addMonths(month, 1), -1));
  // While picking the end, the range follows the pointer.
  const end = to ?? (from && hover && hover >= from ? hover : null);
  const cell = large ? 'size-10' : 'size-8';
  const navBtn = 'grid size-8 place-items-center rounded-lg text-muted hover:bg-raised hover:text-fg disabled:pointer-events-none disabled:opacity-30';
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="flex h-8 items-center justify-between text-[13px] font-semibold">
        {prev ? (
          <button type="button" onClick={prev} aria-label="Previous month" className={navBtn}>
            <Icon name="right" size={16} className="rotate-180" />
          </button>
        ) : (
          <span className="size-8" />
        )}
        <span aria-live="polite">
          {MONTHS[m - 1]} {y}
        </span>
        {next ? (
          <button type="button" onClick={next} disabled={!canNext} aria-label="Next month" className={navBtn}>
            <Icon name="right" size={16} />
          </button>
        ) : (
          <span className="size-8" />
        )}
      </div>
      <div role="grid" aria-label={`${MONTHS[m - 1]} ${y}`} className="grid grid-cols-7 gap-y-0.5">
        {DOW.map((d) => (
          <div key={d} role="columnheader" className="h-6 text-center text-[10.5px] leading-6 tracking-wide text-muted uppercase">
            {d}
          </div>
        ))}
        {Array.from({ length: lead }, (_, i) => (
          <span key={`l${i}`} />
        ))}
        {Array.from({ length: count }, (_, i) => {
          const day = addDays(month, i);
          const off = disabled(day);
          const isStart = day === from;
          const isEnd = day === end || (isStart && !end);
          const inRange = !!from && !!end && day >= from && day <= end;
          return (
            <button
              key={day}
              type="button"
              role="gridcell"
              data-day={day}
              tabIndex={day === focus ? 0 : -1}
              disabled={off}
              aria-selected={inRange || isStart}
              aria-current={day === today ? 'date' : undefined}
              aria-label={`${i + 1} ${MONTHS[m - 1]} ${y}`}
              onClick={() => onPick(day)}
              onPointerEnter={() => onHover(day)}
              className={cx('group relative h-9 p-0 text-[12.5px] tabular-nums outline-none disabled:cursor-not-allowed', large && 'h-11 text-[14px]', off ? 'text-muted opacity-40' : 'text-fg')}
            >
              {inRange && !(isStart && isEnd) && <i aria-hidden="true" className={cx('absolute inset-y-0.5 bg-accent-bg', isStart ? 'right-0 left-1/2' : isEnd ? 'right-1/2 left-0' : 'inset-x-0', !to && 'opacity-60')} />}
              <span
                className={cx(
                  'relative mx-auto grid place-items-center rounded-lg group-focus-visible:outline-2 group-focus-visible:outline-accent',
                  cell,
                  isStart || isEnd ? 'bg-accent font-semibold text-white' : !off && 'group-hover:bg-raised',
                  day === today && !(isStart || isEnd) && 'ring-1 ring-line-strong ring-inset',
                )}
              >
                {i + 1}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Keyboard on a calendar: arrows move a day / week, PageUp / PageDown a month, Home / End the week. */
function moveFocus(key: string, day: string): string | null {
  switch (key) {
    case 'ArrowLeft':
      return addDays(day, -1);
    case 'ArrowRight':
      return addDays(day, 1);
    case 'ArrowUp':
      return addDays(day, -7);
    case 'ArrowDown':
      return addDays(day, 7);
    case 'PageUp':
      return addMonths(day, -1);
    case 'PageDown':
      return addMonths(day, 1);
    case 'Home':
      return addDays(day, -weekday(day));
    case 'End':
      return addDays(day, 6 - weekday(day));
    default:
      return null;
  }
}

/** Months shown, focus and keyboard for a calendar of `count` months whose last month can't pass `max`. */
function useCalendar(anchor: string, count: number, max: string | undefined, min: string | undefined) {
  const first = (day: string) => {
    let m = addMonths(monthStart(day), -(count - 1));
    if (min && m < monthStart(min)) m = monthStart(min);
    return m;
  };
  const [view, setView] = useState(() => first(anchor));
  const [focus, setFocus] = useState(anchor);
  const grid = useRef<HTMLDivElement>(null);
  const last = addMonths(view, count - 1);
  const show = (day: string) => {
    if (day < view) setView(monthStart(day));
    else if (day >= addMonths(last, 1)) setView(addMonths(monthStart(day), -(count - 1)));
  };
  const canNext = !max || addMonths(last, 1) <= max;
  const canPrev = !min || view > monthStart(min);
  const onKeyDown = (e: React.KeyboardEvent) => {
    const day = (e.target as HTMLElement).dataset?.day;
    if (!day) return;
    let to = moveFocus(e.key, day);
    if (!to) return;
    e.preventDefault();
    if (max && to > max) to = max;
    if (min && to < min) to = min;
    setFocus(to);
    show(to);
    requestAnimationFrame(() => grid.current?.querySelector<HTMLElement>(`[data-day="${to}"]`)?.focus());
  };
  return {
    view,
    months: Array.from({ length: count }, (_, i) => addMonths(view, i)),
    focus,
    grid,
    onKeyDown,
    canNext,
    prev: canPrev ? () => setView(addMonths(view, -1)) : undefined,
    next: () => canNext && setView(addMonths(view, 1)),
    jump: (day: string) => {
      setFocus(day);
      setView(first(day));
    },
    setFocus,
  };
}

// ---- Typed day field ----

function TypedDay({ label, value, active, bad, onFocus, onDay, onBad, large, id }: { label: string; value: string | null; active?: boolean; bad?: boolean; onFocus?: () => void; onDay: (day: string) => void; onBad: (why: string) => void; large?: boolean; id: string }) {
  const [text, setText] = useState(dayToTyped(value ?? ''));
  useEffect(() => setText(dayToTyped(value ?? '')), [value]);
  const commit = () => {
    if (text === dayToTyped(value ?? '')) return;
    const d = typedToDay(text);
    if (d) onDay(d);
    else onBad('Type the date as DD/MM/YYYY.');
  };
  return (
    <label htmlFor={id} className={cx('flex min-w-0 flex-col gap-1 text-muted', large ? 'text-[13px]' : 'text-[11px]')}>
      {label}
      <input
        id={id}
        inputMode="numeric"
        autoComplete="off"
        placeholder="DD/MM/YYYY"
        value={text}
        onFocus={onFocus}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
        className={cx(
          'num w-full min-w-0 rounded-lg border bg-bg px-2.5 text-fg outline-none',
          large ? 'h-11 text-[15px]' : 'h-8 text-[12.5px]',
          bad ? 'border-down' : active ? 'border-accent ring-3 ring-accent-bg' : 'border-line-strong focus:border-accent',
        )}
      />
    </label>
  );
}

// ---- Range panel ----

export function RangePanel({ initial, onApply, onCancel, zone = 'local', min, maxDays, times: allowTimes, months = 2, large }: RangeOptions & { initial?: DayRange | null; onApply: (r: DayRange) => void; onCancel: () => void; months?: 1 | 2; large?: boolean }) {
  const today = todayIn(zone);
  const max = today;
  const id = useId();
  const [from, setFrom] = useState<string | null>(initial?.from ?? addDays(today, -6));
  const [to, setTo] = useState<string | null>(initial?.to ?? today);
  const [picking, setPicking] = useState<'from' | 'to'>('from');
  const [hover, setHover] = useState<string | null>(null);
  const [typedBad, setTypedBad] = useState<{ field: 'from' | 'to'; why: string } | null>(null);
  const [times, setTimes] = useState(!!(initial?.fromTime || initial?.toTime));
  const [fromTime, setFromTime] = useState(initial?.fromTime ?? '00:00');
  const [toTime, setToTime] = useState(initial?.toTime ?? '23:59');
  const cal = useCalendar(to ?? today, months, max, min);

  const range: Partial<DayRange> = { from: from ?? undefined, to: to ?? undefined, ...(times ? { fromTime, toTime } : {}) };
  const limits = { min, max, maxDays };
  const problem = typedBad?.why ?? (from && to ? rangeProblem(range, limits) : null) ?? (times && (!isTime(fromTime) || !isTime(toTime)) ? 'Pick both times.' : null);
  const ready = !!from && !!to && !problem;
  const active = RANGE_SHORTCUTS.find((s) => {
    const r = s.range(today);
    return r.from === from && r.to === to;
  })?.label;

  const pick = (day: string) => {
    setTypedBad(null);
    if (picking === 'from' || !from || day < from) {
      setFrom(day);
      setTo(null);
      setPicking('to');
    } else {
      setTo(day);
      setPicking('from');
    }
    cal.setFocus(day);
  };
  const shortcut = (label: string) => {
    const r = RANGE_SHORTCUTS.find((s) => s.label === label)!.range(today);
    setFrom(r.from);
    setTo(r.to);
    setPicking('from');
    setTypedBad(null);
    cal.jump(r.to);
  };
  const typed = (field: 'from' | 'to') => (day: string) => {
    setTypedBad(null);
    if (field === 'from') setFrom(day);
    else setTo(day);
    setPicking(field === 'from' ? 'to' : 'from');
    cal.jump(day);
  };
  const apply = () => ready && onApply({ from: from!, to: to!, ...(times ? { fromTime, toTime } : {}) });

  const tooLong = (label: string) => {
    if (!maxDays && !min) return false;
    const r = RANGE_SHORTCUTS.find((s) => s.label === label)!.range(today);
    return !!rangeProblem(r, limits);
  };
  const summary = problem ? (
    <span role="alert" className="text-down">
      {problem}
    </span>
  ) : !to ? (
    <span>
      Now pick the <b className="font-semibold text-fg">To</b> day
    </span>
  ) : (
    <span>
      <b className="font-semibold text-fg">{rangeLabel(range as DayRange, today)}</b> · {spanDays(from!, to)} day{spanDays(from!, to) === 1 ? '' : 's'}
      {maxDays ? ` · up to ${maxDays}` : ''} · {zone === 'utc' ? 'UTC days' : `your time (${zoneLabel(zone)})`}
    </span>
  );

  const fields = (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
        <TypedDay id={`${id}-from`} label="From" value={from} active={picking === 'from'} bad={typedBad?.field === 'from'} onFocus={() => setPicking('from')} onDay={typed('from')} onBad={(why) => setTypedBad({ field: 'from', why })} large={large} />
        <span className={cx('text-muted', large ? 'pb-3' : 'pb-1.5')} aria-hidden="true">
          →
        </span>
        <TypedDay id={`${id}-to`} label="To" value={to} active={picking === 'to'} bad={typedBad?.field === 'to'} onFocus={() => setPicking('to')} onDay={typed('to')} onBad={(why) => setTypedBad({ field: 'to', why })} large={large} />
      </div>
      {times && (
        <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
          {(
            [
              ['From time', fromTime, setFromTime],
              ['To time', toTime, setToTime],
            ] as const
          ).map(([label, v, set], i) => (
            <label key={label} className={cx('flex min-w-0 flex-col gap-1 text-muted', large ? 'text-[13px]' : 'text-[11px]', i === 1 && 'col-start-3')}>
              {label}
              <input type="time" value={v} onChange={(e) => set(e.target.value)} className={cx('num w-full min-w-0 rounded-lg border border-line-strong bg-bg px-2.5 text-fg outline-none focus:border-accent', large ? 'h-11 text-[15px]' : 'h-8 text-[12.5px]')} />
            </label>
          ))}
        </div>
      )}
    </div>
  );

  const calendar = (
    <div ref={cal.grid} onKeyDown={cal.onKeyDown} onPointerLeave={() => setHover(null)} className={cx('grid gap-5', months === 2 && 'grid-cols-2')}>
      {cal.months.map((m, i) => (
        <Month
          key={m}
          month={m}
          today={today}
          from={from}
          to={to}
          hover={picking === 'to' ? hover : null}
          focus={cal.focus}
          disabled={(d) => d > max || (!!min && d < min)}
          onPick={pick}
          onHover={setHover}
          prev={i === 0 ? cal.prev : undefined}
          next={i === months - 1 ? cal.next : undefined}
          canNext={cal.canNext}
          large={large}
        />
      ))}
    </div>
  );

  const btn = cx('rounded-lg font-semibold', large ? 'h-12 text-[15px]' : 'h-8 px-4 text-[12.5px]');
  const buttons = (
    <div className={cx('gap-2', large ? 'grid grid-cols-[1fr_2fr]' : 'flex')}>
      <button type="button" onClick={onCancel} className={cx(btn, 'bg-raised text-fg')}>
        Cancel
      </button>
      <button type="submit" disabled={!ready} className={cx(btn, 'bg-fg text-bg disabled:opacity-45')}>
        Apply
      </button>
    </div>
  );
  const timesToggle = allowTimes && (
    <label className="flex items-center gap-2 text-[12px] text-muted">
      <input type="checkbox" checked={times} onChange={(e) => setTimes(e.target.checked)} className="accent-[var(--accent)]" />
      Include times
    </label>
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    apply();
  };

  if (large)
    return (
      <form onSubmit={submit} className="flex flex-col gap-3">
        <div className="no-scrollbar -mx-1 flex gap-1.5 overflow-x-auto px-1">
          {RANGE_SHORTCUTS.map((s) => (
            <button
              key={s.label}
              type="button"
              disabled={tooLong(s.label)}
              onClick={() => shortcut(s.label)}
              className={cx('h-9 shrink-0 rounded-full border px-3.5 text-[13px] font-medium disabled:opacity-40', active === s.label ? 'border-accent bg-accent-bg text-accent-fg' : 'border-line text-muted')}
            >
              {s.label}
            </button>
          ))}
        </div>
        {fields}
        {calendar}
        <div className="text-[12.5px] text-muted">{summary}</div>
        {timesToggle}
        {buttons}
      </form>
    );

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-[148px_minmax(0,1fr)]">
      <div className="flex flex-wrap gap-px border-b border-line bg-panel p-1.5 sm:flex-col sm:flex-nowrap sm:border-r sm:border-b-0">
        <div className="hidden px-2.5 pt-1.5 pb-1 text-[10.5px] font-semibold tracking-wider text-muted uppercase sm:block">Shortcuts</div>
        {RANGE_SHORTCUTS.map((s) => (
          <button
            key={s.label}
            type="button"
            disabled={tooLong(s.label)}
            onClick={() => shortcut(s.label)}
            className={cx('h-[30px] rounded-[7px] px-2.5 text-left text-[13px] disabled:opacity-40', active === s.label ? 'bg-accent-bg font-semibold text-accent-fg' : 'text-fg hover:bg-raised')}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div className="flex min-w-0 flex-col gap-3 p-4">
        {fields}
        {calendar}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3">
          <div className="flex min-w-0 flex-col gap-1.5 text-[12px] text-muted">
            {summary}
            {timesToggle}
          </div>
          {buttons}
        </div>
      </div>
    </form>
  );
}

// ---- Floating layer: a popover under its trigger, or a bottom sheet on a phone ----

const EDGE = 12;

function Floating({ anchor, onClose, title, children, align = 'end', narrow }: { anchor: RefObject<HTMLElement | null>; onClose: () => void; title: string; children: (layout: { large: boolean; months: 1 | 2 }) => ReactNode; align?: 'start' | 'end'; narrow?: boolean }) {
  const phone = useMedia(PHONE_QUERY);
  const two = useMedia(TWO_MONTHS_QUERY);
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (phone) return;
    const place = () => {
      const a = anchor.current?.getBoundingClientRect();
      const b = box.current?.getBoundingClientRect();
      if (!a || !b) return;
      const vw = document.documentElement.clientWidth;
      const vh = window.innerHeight;
      let left = align === 'end' ? a.right - b.width : a.left;
      left = Math.max(EDGE, Math.min(left, vw - b.width - EDGE));
      let top = a.bottom + 6;
      if (top + b.height > vh - EDGE && a.top - 6 - b.height > EDGE) top = a.top - 6 - b.height;
      setPos({ left, top: Math.max(EDGE, top) });
    };
    place();
    const ro = new ResizeObserver(place);
    if (box.current) ro.observe(box.current);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor, phone, align]);

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    box.current?.querySelector<HTMLElement>('[tabindex="0"][data-day], input, button')?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && (e.stopPropagation(), close.current());
    const away = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!box.current?.contains(t) && !anchor.current?.contains(t)) close.current();
    };
    window.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', away);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', away);
      prev?.focus?.({ preventScroll: true });
    };
  }, [anchor]);

  if (phone)
    return createPortal(
      <div className="fixed inset-0 z-[60] grid place-items-end bg-scrim">
        <div ref={box} role="dialog" aria-modal="true" aria-label={title} className="shadow-pop max-h-[92dvh] w-full overflow-y-auto rounded-t-[22px] bg-bg px-4 pt-2 pb-[max(16px,env(safe-area-inset-bottom))]">
          <div className="mx-auto mb-2 h-1 w-9 rounded-full bg-line-strong" aria-hidden="true" />
          <div className="mb-3 flex items-center justify-between">
            <h2 className="m-0 text-[17px] font-semibold">{title}</h2>
            <button type="button" onClick={onClose} aria-label="Close" className="grid size-9 place-items-center rounded-full bg-raised text-fg">
              <Icon name="close" size={14} />
            </button>
          </div>
          {children({ large: true, months: 1 })}
        </div>
      </div>,
      document.body,
    );
  return createPortal(
    <div
      ref={box}
      role="dialog"
      aria-label={title}
      style={{ left: pos?.left ?? 0, top: pos?.top ?? 0, visibility: pos ? 'visible' : 'hidden' }}
      className={cx('shadow-pop fixed z-[60] max-h-[calc(100dvh-24px)] overflow-hidden overflow-y-auto rounded-xl border border-line bg-bg', narrow ? 'w-auto' : two ? 'w-[640px]' : 'w-[min(380px,calc(100vw-24px))]')}
    >
      {children({ large: false, months: narrow || !two ? 1 : 2 })}
    </div>,
    document.body,
  );
}

/** The range panel as a popover anchored to `anchor` (a bottom sheet on a phone). */
export function RangePopover({ anchor, value, onApply, onClose, align, ...opts }: RangeOptions & { anchor: RefObject<HTMLElement | null>; value: DayRange | null; onApply: (r: DayRange) => void; onClose: () => void; align?: 'start' | 'end' }) {
  return (
    <Floating anchor={anchor} onClose={onClose} title="Custom range" align={align}>
      {({ large, months }) => (
        <RangePanel
          {...opts}
          initial={value}
          large={large}
          months={months}
          onCancel={onClose}
          onApply={(r) => {
            onApply(r);
            onClose();
          }}
        />
      )}
    </Floating>
  );
}

// ---- Triggers ----

/** A preset, or a custom range (`id: 'custom'`). */
export type Period<T extends string | number = string> = { id: T } | { id: 'custom'; range: DayRange };
export const isCustom = <T extends string | number>(p: Period<T>): p is { id: 'custom'; range: DayRange } => p.id === 'custom' && 'range' in p;

/** The last `days` days, today included, as a range. */
export const lastDays = (days: number, zone: Zone = 'local'): DayRange => ({ from: addDays(todayIn(zone), -(days - 1)), to: todayIn(zone) });

/** The range a period covers: a custom range as is, a preset through `days` (0 or undefined = all time, null). */
export function periodRangeOf<T extends string | number>(p: Period<T>, days: (id: T) => number | undefined, zone: Zone = 'local'): DayRange | null {
  if (isCustom(p)) return p.range;
  const n = days(p.id as T);
  return n ? lastDays(n, zone) : null;
}

export function periodText<T extends string | number>(p: Period<T>, presets: { id: T; label: string }[]): string {
  return isCustom(p) ? rangeLabel(p.range) : (presets.find((x) => x.id === p.id)?.label ?? '');
}

/**
 * Time-frame dropdown: presets with a check on the chosen one, then "Custom range…" under a
 * divider. With a custom range applied the trigger shows the range itself.
 */
export function TimeFrameSelect<T extends string | number>({ presets, value, onChange, label = 'Time frame', className, triggerClassName, ...opts }: RangeOptions & { presets: { id: T; label: string }[]; value: Period<T>; onChange: (p: Period<T>) => void; label?: string; className?: string; /** Replaces the trigger's size and shape (the account area's larger pills). */ triggerClassName?: string }) {
  const [open, setOpen] = useState<'menu' | 'range' | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (open !== 'menu') return;
    const away = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && setOpen(null);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(null);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const text = periodText(value, presets);
  const custom = isCustom(value);
  const opt = (selected: boolean) => cx('flex h-9 w-full items-center justify-between px-3.5 text-left text-[13px] text-fg hover:bg-panel', selected && 'font-semibold');
  return (
    <div ref={ref} className={cx('relative', className)}>
      <button
        ref={trigger}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open !== null}
        aria-label={`${label}: ${text}`}
        onClick={() => setOpen((o) => (o ? null : 'menu'))}
        className={cx('flex items-center justify-between gap-2 whitespace-nowrap text-fg', triggerClassName ?? cx('h-7 min-w-[108px] rounded-lg border bg-bg px-2.5 text-xs', open ? 'border-accent' : 'border-line-strong'))}
      >
        <span>{text}</span>
        <Icon name="chevron" size={14} className={cx('text-muted transition-transform', open === 'menu' && 'rotate-180')} />
      </button>
      {open === 'menu' && (
        <div className="shadow-pop absolute top-full right-0 z-20 mt-1 w-44 rounded-[10px] border border-line bg-bg py-1.5">
          <ul role="listbox" aria-label={label} className="m-0 list-none p-0">
            {presets.map((p) => (
              <li key={String(p.id)} role="option" aria-selected={!custom && p.id === value.id}>
                <button
                  type="button"
                  onClick={() => {
                    onChange({ id: p.id });
                    setOpen(null);
                  }}
                  className={opt(!custom && p.id === value.id)}
                >
                  {p.label}
                  {!custom && p.id === value.id && <Icon name="check" size={16} className="text-accent" />}
                </button>
              </li>
            ))}
            <li role="separator" className="my-1.5 border-t border-line" />
            <li role="option" aria-selected={custom}>
              <button type="button" onClick={() => setOpen('range')} className={opt(custom)}>
                Custom range…
                {custom && <Icon name="check" size={16} className="text-accent" />}
              </button>
            </li>
          </ul>
        </div>
      )}
      {open === 'range' && <RangePopover {...opts} anchor={trigger} value={custom ? value.range : null} onApply={(range) => onChange({ id: 'custom', range })} onClose={() => setOpen(null)} />}
    </div>
  );
}

/**
 * Calendar tab for a row of period tabs (charts): opens the range panel; with a range applied it
 * shows the range and the row's own tabs go unselected.
 */
export function CustomRangeTab({ value, onApply, className, activeClassName, idleClassName, ...opts }: RangeOptions & { value: DayRange | null; onApply: (r: DayRange) => void; className?: string; activeClassName: string; idleClassName: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        ref={ref}
        type="button"
        role="radio"
        aria-checked={!!value}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={value ? `Custom range: ${rangeLabel(value)}` : 'Custom range'}
        title="Custom range"
        onClick={() => setOpen((o) => !o)}
        className={cx('inline-flex items-center justify-center gap-1.5 whitespace-nowrap', className, value ? activeClassName : idleClassName)}
      >
        <Icon name="calendar" size={14} />
        {value && <span>{rangeLabel(value)}</span>}
      </button>
      {open && <RangePopover {...opts} anchor={ref} value={value} onApply={onApply} onClose={() => setOpen(false)} />}
    </>
  );
}

// ---- Single day field ----

/**
 * A date field for forms: type `DD/MM/YYYY` or pick from the calendar. `value` / `onChange` use
 * `YYYY-MM-DD` (or `YYYY-MM-DDTHH:MM` with `withTime`), the same as the native inputs it replaces.
 */
export function DateField({ value, onChange, min, max, withTime, id, className, buttonClassName, readOnly, disabled, yearPicker, autoComplete, 'aria-label': ariaLabel }: { value: string; onChange: (v: string) => void; min?: string; max?: string; withTime?: boolean; id?: string; className?: string; buttonClassName?: string; readOnly?: boolean; disabled?: boolean; yearPicker?: boolean; autoComplete?: string; 'aria-label'?: string }) {
  const day = value.slice(0, 10);
  const time = withTime ? value.slice(11, 16) : '';
  const [text, setText] = useState(dayToTyped(day));
  const [bad, setBad] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setText(dayToTyped(day));
    setBad(false);
  }, [day]);
  const minDay = min?.slice(0, 10);
  const maxDay = max?.slice(0, 10);
  const out = (d: string) => (!!minDay && d < minDay) || (!!maxDay && d > maxDay);
  const emit = (d: string, t = time) => onChange(withTime ? `${d}T${t || '00:00'}` : d);
  const commit = () => {
    if (text === dayToTyped(day)) return;
    if (!text.trim()) {
      setBad(false);
      return onChange('');
    }
    const d = typedToDay(text);
    if (!d || out(d)) return setBad(true);
    setBad(false);
    emit(d);
  };
  const locked = readOnly || disabled;
  return (
    <div ref={wrap} className="relative flex w-full min-w-0 items-stretch gap-1.5">
      <input
        id={id}
        aria-label={ariaLabel}
        aria-invalid={bad || undefined}
        inputMode="numeric"
        autoComplete={autoComplete ?? 'off'}
        placeholder="DD/MM/YYYY"
        readOnly={readOnly}
        disabled={disabled}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), commit())}
        className={cx(className, 'num min-w-0 flex-1', bad && 'border-down!')}
      />
      {withTime && (
        <input
          type="time"
          aria-label={`${ariaLabel ?? 'Date'} time`}
          readOnly={readOnly}
          disabled={disabled || !day}
          value={time}
          onChange={(e) => day && emit(day, e.target.value)}
          className={cx(className, 'num w-[116px]! flex-none')}
        />
      )}
      {!locked && <CalendarButton anchor={wrap} value={day || null} min={minDay} max={maxDay} yearPicker={yearPicker} onPick={emit} className={buttonClassName ?? 'grid w-9 flex-none place-items-center rounded-lg border border-line-strong text-muted hover:text-fg'} />}
    </div>
  );
}

/** A calendar button that opens a one-month day picker; for fields that keep their own input. */
export function CalendarButton({ value, onPick, min, max, yearPicker, className, anchor }: { value: string | null; onPick: (day: string) => void; min?: string; max?: string; yearPicker?: boolean; className?: string; anchor?: RefObject<HTMLElement | null> }) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button ref={btn} type="button" aria-label="Open calendar" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((o) => !o)} className={className}>
        <Icon name="calendar" size={15} />
      </button>
      {open && (
        <Floating anchor={anchor ?? btn} onClose={() => setOpen(false)} title="Pick a date" align="start" narrow>
          {({ large }) => (
            <DayPicker
              value={value}
              min={min}
              max={max}
              large={large}
              yearPicker={yearPicker}
              onPick={(d) => {
                onPick(d);
                setOpen(false);
              }}
            />
          )}
        </Floating>
      )}
    </>
  );
}

function DayPicker({ value, min, max, large, yearPicker, onPick }: { value: string | null; min?: string; max?: string; large: boolean; yearPicker?: boolean; onPick: (d: string) => void }) {
  const today = todayIn();
  const start = value ?? (max && max < today ? max : min && min > today ? min : today);
  const cal = useCalendar(start, 1, max, min);
  const y = +cal.view.slice(0, 4);
  const lo = min ? +min.slice(0, 4) : y - 100;
  const hi = max ? +max.slice(0, 4) : y + 10;
  const sel = 'h-8 rounded-lg border border-line-strong bg-bg px-2 text-[12.5px] text-fg';
  return (
    <div className={cx('flex flex-col gap-2', large ? '' : 'w-[300px] max-w-full p-3')}>
      {yearPicker && (
        <div className="grid grid-cols-2 gap-2">
          <select aria-label="Month" value={+cal.view.slice(5, 7)} onChange={(e) => cal.jump(`${cal.view.slice(0, 4)}-${String(e.target.value).padStart(2, '0')}-01`)} className={sel}>
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
          <select aria-label="Year" value={y} onChange={(e) => cal.jump(`${e.target.value}${cal.view.slice(4)}`)} className={cx(sel, 'num')}>
            {Array.from({ length: hi - lo + 1 }, (_, i) => hi - i).map((yy) => (
              <option key={yy} value={yy}>
                {yy}
              </option>
            ))}
          </select>
        </div>
      )}
      <div ref={cal.grid} onKeyDown={cal.onKeyDown}>
        <Month
          month={cal.view}
          today={today}
          from={value}
          to={value}
          hover={null}
          focus={cal.focus}
          disabled={(d) => (!!min && d < min) || (!!max && d > max)}
          onPick={onPick}
          onHover={() => {}}
          prev={cal.prev}
          next={cal.next}
          canNext={cal.canNext}
          large={large}
        />
      </div>
      {value && <div className="text-[12px] text-muted">Selected: {dayLabel(value, '')}</div>}
    </div>
  );
}
